import _ from 'lodash';
import React, {useCallback, useContext, useMemo} from 'react';

import {Analytics} from '../../../services/analytics/Analytics';
import {useViewer} from '../../../state/viewer/hooks';
import {Pagination} from '../../Pagination';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {RegistriesSearchContext} from '../common/RegistriesSearchContextProvider';
import {StarredRegistriesContext} from '../common/StarredRegistriesContextProvider';
import {
  useRegistriesWithMembership,
  useSearchablePaginatedRegistries,
} from './hooks';
import {NewRegistryCard} from './NewRegistryCard';
import {RegistryCard} from './RegistryCard';

const PAGE_SIZE = 6;

type AllRegistriesProps = {
  orgName: string;
  readOnly: boolean;
  // Temporary
  // The CreateRegistryDrawer checks for existing registry names, so we populate them here.
  // But this is limited by pagination, since we're not fetching all registries at once.
  setExistingRegistryNames: (registryNames: Set<string>) => void;
};

export const AllRegistries = ({
  orgName,
  readOnly,
  setExistingRegistryNames,
}: AllRegistriesProps) => {
  const {searchFilter} = useContext(RegistriesSearchContext);
  const {starredRegistries: starredRegistriesMap} = useContext(
    StarredRegistriesContext
  );
  const starredRegistryIds = useMemo(
    () => new Set(Object.keys(starredRegistriesMap)),
    [starredRegistriesMap]
  );

  const {
    currentPage: currentPaginatedRegistries,
    loading: registriesLoading,
    refetch: refetchRegistries,
    hasNextPage,
    totalVisibleRegistriesLoaded,
    page,
    onPageChange,
    error,
  } = useSearchablePaginatedRegistries({
    orgName,
    pageSize: PAGE_SIZE,
    starredRegistryIds,
  });
  const viewer = useViewer();

  const registries = useMemo(
    () =>
      _.orderBy(
        currentPaginatedRegistries,
        ['updatedAt', 'createdAt'],
        ['desc', 'desc']
      ),
    [currentPaginatedRegistries]
  );

  useMemo(() => {
    const existingNames = new Set(
      registries.map(registry => registry.registryName)
    );
    setExistingRegistryNames(existingNames);
  }, [registries, setExistingRegistryNames]);

  const {registriesWithMembership} = useRegistriesWithMembership({
    orgName,
    registries,
    viewerId: viewer?.id,
  });

  // Wrap onPageChange to add analytics tracking
  const handlePageChange = useCallback(
    (newPage: number) => {
      Analytics.track('Registry pagination interaction', {
        location: 'Registries homepage - All registries',
        organizationName: orgName,
        pageNumber: newPage,
      });
      onPageChange(newPage);
    },
    [onPageChange, orgName]
  );

  return (
    <div className="w-full border-t border-moon-200 pb-32 pt-32">
      <div className="flex flex-row items-center justify-between">
        <div className="mb-16 text-base font-semibold text-moon-550">
          All registries
        </div>
        {!registriesLoading && !error && (
          <Pagination
            itemCount={totalVisibleRegistriesLoaded}
            page={page}
            perPage={PAGE_SIZE}
            onPageChange={handlePageChange}
            allItemsLoaded={!hasNextPage}
          />
        )}
      </div>
      {registriesLoading ? (
        <div>
          <Loader name="global-registry-landing-page-collections" size="huge" />
        </div>
      ) : (
        <div className="grid grid-cols-1 items-center gap-14 whitespace-nowrap sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {!registries.length && !searchFilter && !readOnly ? (
            <NewRegistryCard />
          ) : !registries.length && searchFilter ? (
            <div className="text-base text-moon-550">No registries found</div>
          ) : (
            registriesWithMembership.map(registry => {
              return (
                <RegistryCard
                  key={registry.registryName}
                  {...registry}
                  canViewCollectionCount={registry.canViewCollectionCount}
                  isRegistryAvailableToJoin={registry.isRegistryAvailableToJoin}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
